import LegalLayout from "./LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="February 2026">
      <Section title="1. Introduction">
        <p>
          Shadow Bridge Technologies, Inc. ("<strong>we</strong>," "<strong>us</strong>," or "<strong>PROX</strong>")
          operates the PROX privacy communication service accessible at prox.day and via SMS/MMS messaging.
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you
          use our service.
        </p>
        <p>
          By using PROX, you agree to the collection and use of information in accordance with this policy.
          If you do not agree, please do not use the service.
        </p>
      </Section>

      <Section title="2. Information We Collect">
        <p>We collect the following categories of information:</p>
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li>
            <strong className="text-zinc-200">Phone Numbers</strong> — Your real phone number and assigned proxy
            number, necessary to route messages through our Shadow Mapping system.
          </li>
          <li>
            <strong className="text-zinc-200">Message Metadata</strong> — Sender/recipient phone numbers,
            timestamps, and message routing data. We do <em>not</em> store message content after delivery.
          </li>
          <li>
            <strong className="text-zinc-200">Mapping Data</strong> — The association between your real number,
            shadow numbers, and contacts, required to maintain active conversations.
          </li>
          <li>
            <strong className="text-zinc-200">Usage Data</strong> — Service interactions such as BURN, RESET,
            SAVE, STOP, and HELP commands.
          </li>
          <li>
            <strong className="text-zinc-200">Account Information</strong> — Email address and billing details
            if you create an account.
          </li>
        </ul>
      </Section>

      <Section title="3. How We Use Your Information">
        <p>We use collected information to:</p>
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li>Route and deliver SMS/MMS messages through our proxy system.</li>
          <li>Maintain and manage Shadow Number mappings.</li>
          <li>Process commands (BURN, RESET, SAVE, HELP, STOP).</li>
          <li>Generate vCard contact files for new conversations.</li>
          <li>Prevent abuse, fraud, and unauthorized access.</li>
          <li>Comply with legal obligations and law enforcement requests.</li>
          <li>Improve and maintain the service.</li>
        </ul>
      </Section>

      <Section title="4. Message Content">
        <p>
          PROX acts as a pass-through relay. Message content is forwarded in real-time and is <strong>not
          stored</strong> on our servers after successful delivery. We retain message metadata (sender, recipient,
          timestamp, delivery status) for operational and compliance purposes.
        </p>
      </Section>

      <Section title="5. Data Sharing">
        <p>We may share your information with:</p>
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li>
            <strong className="text-zinc-200">Telecommunications Providers</strong> — Telnyx or similar carriers
            to deliver messages. These providers process phone numbers and message content in transit.
          </li>
          <li>
            <strong className="text-zinc-200">Law Enforcement</strong> — When required by valid legal process
            (subpoena, court order, or applicable law).
          </li>
          <li>
            <strong className="text-zinc-200">Service Providers</strong> — Infrastructure and hosting providers
            bound by confidentiality agreements.
          </li>
        </ul>
        <p>We do <strong>not</strong> sell your personal information to third parties.</p>
      </Section>

      <Section title="6. Data Retention">
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li>Active mappings are retained while the mapping is in use.</li>
          <li>Burned/deleted mappings are purged within 30 days.</li>
          <li>Inactive mappings may be automatically burned after 14 days of inactivity.</li>
          <li>Message metadata logs are retained for up to 90 days.</li>
          <li>Account data is retained until you request deletion.</li>
        </ul>
      </Section>

      <Section title="7. Data Security">
        <p>
          We implement industry-standard security measures including encrypted transit (TLS), access controls,
          and infrastructure monitoring. However, no method of electronic storage or transmission is 100% secure.
        </p>
      </Section>

      <Section title="8. Your Rights">
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li>Access the personal data we hold about you.</li>
          <li>Request correction or deletion of your data.</li>
          <li>Opt out of SMS messaging by replying STOP to any PROX number.</li>
          <li>Request a copy of your data in a portable format.</li>
        </ul>
        <p>
          To exercise these rights, contact us at{" "}
          <a href="mailto:privacy@prox.day" className="text-white underline underline-offset-2">
            privacy@prox.day
          </a>.
        </p>
      </Section>

      <Section title="9. Children's Privacy">
        <p>
          PROX is not intended for individuals under 18. We do not knowingly collect personal information
          from minors. If you believe a minor has provided us data, contact us to request deletion.
        </p>
      </Section>

      <Section title="10. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Changes will be posted on this page with an
          updated "Last updated" date. Continued use of PROX after changes constitutes acceptance.
        </p>
      </Section>

      <Section title="11. Contact">
        <p>
          Shadow Bridge Technologies, Inc.<br />
          Email:{" "}
          <a href="mailto:privacy@prox.day" className="text-white underline underline-offset-2">
            privacy@prox.day
          </a>
        </p>
      </Section>
    </LegalLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-lg text-white mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
